// pipe: shared ERP-screen page header (SSOT / DRY). A small, pure presenter rendering a title +
// optional subtitle + an optional plain-language ⓘ help tip + optional page ACTIONS (buttons at the
// end of the title row; they wrap under the title on a phone), bound to its own css module. Used by
// every ERP screen so the header markup is declared ONCE.
//
// Import discipline (mirrors the pure view seams): this imports ONLY the css module, React types,
// and the host-provided info-tip slot — no runtime services/DI/workbench — so it renders cleanly
// under happy-dom.
import type { ReactNode } from 'react';

import { ErpInfoTipSlot } from './erp-info-tip-slot';
import * as styles from './erp-page-header.css';

export interface ErpPageHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Plain-language "what is this page / how do I use it" help, shown as an ⓘ tip by the title. */
  info?: ReactNode;
  /** The page's own actions (e.g. the job page's report buttons), at the end of the title row. */
  actions?: ReactNode;
}

export function ErpPageHeader({
  title,
  subtitle,
  info,
  actions,
}: ErpPageHeaderProps) {
  const heading = (
    <h1 className={styles.pageTitle}>
      {title}
      {info != null && info !== '' ? (
        <ErpInfoTipSlot
          content={info}
          side="bottom"
          align="start"
          className={styles.pageInfoTip}
          data-testid="erp-page-info-tip"
        />
      ) : null}
    </h1>
  );
  return (
    <header className={styles.pageHeader}>
      {actions != null && actions !== false ? (
        <div className={styles.titleRow}>
          {heading}
          <div className={styles.pageActions} data-testid="erp-page-actions">
            {actions}
          </div>
        </div>
      ) : (
        heading
      )}
      {subtitle != null && subtitle !== '' ? (
        <p className={styles.pageSubtitle}>{subtitle}</p>
      ) : null}
    </header>
  );
}
